/**
 * Makes everything outside `element` inert, so a modal surface keeps both the tab order
 * and the accessibility tree to itself. Walks up to <body>, marking the siblings at each
 * level, and leaves anything already inert alone so it is not released by mistake.
 * @param {HTMLElement} element the surface that stays reachable
 * @param {HTMLElement[]} alsoKeep parts of the modal that live outside `element`, such as
 *   its backdrop: an inert element takes no pointer events, so inerting one would kill
 *   click-to-close
 * @return {Function} cleanup function, releasing only what this call marked
 */
const modalBoundary = (element, alsoKeep = []) => {
  const keep = [element, ...alsoKeep].filter(Boolean);
  const marked = [];

  for (
    let node = element;
    node?.parentElement && node !== document.body;
    node = node.parentElement
  ) {
    for (const sibling of node.parentElement.children) {
      const belongsToModal = keep.some((kept) => sibling === kept || sibling.contains(kept));
      if (!belongsToModal && !sibling.inert) {
        sibling.inert = true;
        marked.push(sibling);
      }
    }
  }

  return () => {
    marked.forEach((sibling) => {
      sibling.inert = false;
    });
    marked.length = 0;
  };
};

export default modalBoundary;
